import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Te rugăm să completezi numele, email-ul și mesajul.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-request`, form);
      toast.success("Mulțumim! Te vom contacta în cel mai scurt timp.");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-28 bg-gradient-to-b from-[#0A0908] to-[#080706] border-t border-[#1A1411]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-[0.32em] uppercase text-[#C8A97E] mb-6">
            — Contact
          </p>
          <h2 className="font-[Playfair_Display] text-[#FDF8F5] text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08] tracking-tight">
            Cere o ofertă
            <br />
            <em className="italic text-[#C8A97E]">personalizată.</em>
          </h2>
          <p className="mt-8 text-[#A8A29E] text-base font-light leading-relaxed max-w-md">
            Completează formularul și un reprezentant te va contacta în cel
            mai scurt timp cu o ofertă adaptată nevoilor afacerii tale.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="tel:+40700000000"
              className="flex items-center gap-4 group"
              data-testid="contact-phone"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-[#2A2522] group-hover:border-[#C8A97E] transition-colors">
                <Phone size={18} strokeWidth={1.2} className="text-[#C8A97E]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#6B5E55]">Telefon</p>
                <p className="text-[#FDF8F5] font-light mt-0.5">+40 700 000 000</p>
              </div>
            </a>
            <a
              href="mailto:contact@produse-engross.ro"
              className="flex items-center gap-4 group"
              data-testid="contact-email"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-[#2A2522] group-hover:border-[#C8A97E] transition-colors">
                <Mail size={18} strokeWidth={1.2} className="text-[#C8A97E]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#6B5E55]">Email</p>
                <p className="text-[#FDF8F5] font-light mt-0.5">contact@produse-engross.ro</p>
              </div>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-address">
              <span className="flex h-12 w-12 items-center justify-center border border-[#2A2522]">
                <MapPin size={18} strokeWidth={1.2} className="text-[#C8A97E]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#6B5E55]">Sediu</p>
                <p className="text-[#FDF8F5] font-light mt-0.5">București, România</p>
              </div>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-[#0F0D0B] border border-[#2A2522] p-8 sm:p-12"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Nume *" testId="input-name">
              <input
                data-testid="contact-input-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                className="w-full bg-transparent border-b border-[#3D3530] text-[#FDF8F5] py-3 focus:border-[#C8A97E] focus:outline-none transition-colors"
                placeholder="Ion Popescu"
              />
            </Field>
            <Field label="Companie" testId="input-company">
              <input
                data-testid="contact-input-company"
                type="text"
                value={form.company}
                onChange={update("company")}
                className="w-full bg-transparent border-b border-[#3D3530] text-[#FDF8F5] py-3 focus:border-[#C8A97E] focus:outline-none transition-colors"
                placeholder="Restaurant / Magazin / Hotel"
              />
            </Field>
            <Field label="Email *" testId="input-email">
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="w-full bg-transparent border-b border-[#3D3530] text-[#FDF8F5] py-3 focus:border-[#C8A97E] focus:outline-none transition-colors"
                placeholder="email@exemplu.ro"
              />
            </Field>
            <Field label="Telefon" testId="input-phone">
              <input
                data-testid="contact-input-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                className="w-full bg-transparent border-b border-[#3D3530] text-[#FDF8F5] py-3 focus:border-[#C8A97E] focus:outline-none transition-colors"
                placeholder="+40 7XX XXX XXX"
              />
            </Field>
          </div>

          <div className="mt-8">
            <Field label="Mesaj / Produse de interes *" testId="input-message">
              <textarea
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="w-full bg-transparent border-b border-[#3D3530] text-[#FDF8F5] py-3 focus:border-[#C8A97E] focus:outline-none transition-colors resize-none"
                placeholder="Spune-ne ce produse te interesează și ce cantități..."
              />
            </Field>
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="mt-10 inline-flex items-center gap-3 bg-[#C8A97E] text-[#080706] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#B3956D] transition-colors duration-300 disabled:opacity-60"
          >
            <Send size={16} strokeWidth={1.4} />
            {loading ? "Se trimite..." : "Trimite cererea"}
          </button>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-[0.25em] text-[#C8A97E]">{label}</span>
    {children}
  </label>
);
